import React, { useState, useMemo, useEffect } from 'react';
import css from './ListingsTable.module.css';
import { ListingState, ListingStatusSection } from '../../../util/enums';
import { isArrayLength } from '../../../util/genericHelpers';
import { categorizeListings, getActiveTab, setActiveTab } from '../../../util/helperFunctions';
import { DETAILS, EDIT, ListingTableHeaders } from '../../../util/constants';
import { IconSpinner } from '../../../components';
import TableRow from './TableRow';
import ActionBarButtons from './ActionBarButtons';
import { FormattedMessage } from 'react-intl';
import TableSelection from './TableSelection';
import EditListingModal from './EditListingModal';
import TableGrid from './TableGrid';
import { useHistory } from 'react-router-dom/cjs/react-router-dom';
import { useRouteConfiguration } from '../../../context/routeConfigurationContext';
import { createResourceLocatorString } from '../../../util/routes';
import { createSlug } from '../../../util/urlHelpers';
import { useSelector } from 'react-redux';
import { copyListing, copyListingSelector } from '../ShopSettingsPage.duck';
import { showErrorToast, showSuccessToast } from '../../../util/toast';

const ListingsTableMap = {
  [ListingStatusSection.DRAFT]: ListingState.DRAFT,
  [ListingStatusSection.AVAILABLE]: ListingState.PUBLISHED,
  [ListingStatusSection.SOLD]: ListingState.SOLD,
  [ListingStatusSection.CLOSED]: ListingState.CLOSED,
};

const ListingsByState = {
  [ListingState.DRAFT]: categorized => categorized.draftListings || [],
  [ListingState.PUBLISHED]: categorized => categorized.publishedListings || [],
  [ListingState.SOLD]: categorized => categorized.soldListings || [],
  [ListingState.CLOSED]: categorized => categorized.closedListings || [],
};

const ListingsTable = ({
  listings,
  intl,
  dispatch,
  config,
  values,
  form,
  ShopSearchBar,
  isMobileLayout,
  isShopifyAccountPage,
  setActiveState,
  activeState,
  listingsAreLoading,
  shopifyConnected,
}) => {
  const history = useHistory();
  const routeConfiguration = useRouteConfiguration();
  const activeTab = getActiveTab() ?? activeState;
  const copyListingId = useSelector(copyListingSelector);
  const [selectedItems, setSelectedItems] = useState([]);
  const [isEditListingModalOpen, setIsEditListingModalOpen] = useState(false);
  const categorizedListings = useMemo(() => categorizeListings(listings), [listings]);

  const currentListings = useMemo(() => ListingsByState[activeTab]?.(categorizedListings) || [], [
    activeTab,
    categorizedListings,
  ]);

  useEffect(() => {
    if (isShopifyAccountPage && activeTab === ListingState.SOLD) {
      setActiveTab(ListingState.PUBLISHED);
    }
  }, [activeTab, isShopifyAccountPage]);

  useEffect(() => {
    if (activeTab && activeTab !== activeState) {
      setActiveState(activeTab);
    }
  }, [activeTab]);

  const handleState = state => {
    const newActiveState = ListingsTableMap[state];
    setActiveTab(newActiveState);
    setActiveState(newActiveState);
    setSelectedItems([]);
  };

  const handleSelectItem = itemId => {
    setSelectedItems(prev =>
      prev.includes(itemId) ? prev.filter(id => id !== itemId) : [...prev, itemId]
    );
  };

  const currentListingIds = useMemo(() => currentListings.map(listing => listing.id.uuid), [
    currentListings,
  ]);
  const hasCurrentListings = isArrayLength(currentListingIds);
  const areAllCurrentListingsSelected =
    hasCurrentListings && currentListingIds.every(id => selectedItems.includes(id));

  const handleSelectAllCurrentListings = () => {
    setSelectedItems(prev => {
      if (areAllCurrentListingsSelected) {
        return prev.filter(id => !currentListingIds.includes(id));
      }

      return Array.from(new Set([...prev, ...currentListingIds]));
    });
  };

  const getTabState = statusSection => ListingsTableMap[statusSection];

  const openModal = () => setIsEditListingModalOpen(true);

  const navigateToEditListingPage = (listingId, title) => {
    history.push(
      createResourceLocatorString(
        'EditListingPage',
        routeConfiguration,
        { id: listingId, type: EDIT, slug: createSlug(title), tab: DETAILS },
        {}
      )
    );
  };

  const navigateToCopyListingPage = async listing => {
    try {
      const response = await dispatch(copyListing(listing));
      const listingTitle = response?.attributes?.title;
      const listingId = response?.id?.uuid;
      navigateToEditListingPage(listingId, listingTitle);
      setTimeout(() => {
        showSuccessToast('Listing copied successfully');
      }, 2000);
    } catch (error) {
      showErrorToast('Error while copying the listing. Please try again.');
    }
  };

  const prepareListingProps = listing => {
    const { id, attributes, images, currentStock } = listing;
    const { title, state, createdAt, price } = attributes;
    const imageUrl = images?.[0]?.attributes?.variants['listing-card']?.url || '';
    return {
      id,
      title,
      imageUrl,
      price,
      state,
      createdAt,
      currentStock,
      listing,
    };
  };

  const commonProps = {
    handleSelectItem,
    intl,
    selectedItems,
    navigateToEditListingPage,
    navigateToCopyListingPage,
    copyListingId,
  };

  const listingsContent = useMemo(() => {
    if (listingsAreLoading) {
      const loadingContent = (
        <div className={css.loadingListings}>
          <IconSpinner />
          <span>Loading listings...</span>
        </div>
      );

      return isMobileLayout ? (
        <div className={css.loadingCell}>{loadingContent}</div>
      ) : (
        <tr>
          <td colSpan={8} className={css.loadingCell}>
            {loadingContent}
          </td>
        </tr>
      );
    }

    if (!isArrayLength(currentListings)) {
      const noListingsMsg = (
        <FormattedMessage id="ShopSettingsForm.noListings" values={{ activeState: activeTab }} />
      );
      return isMobileLayout ? (
        <div colSpan={8} style={{ textAlign: 'center' }} className={css.noListings}>
          {noListingsMsg}
        </div>
      ) : (
        <tr>
          <td colSpan={8} style={{ textAlign: 'center' }} className={css.noListings}>
            {noListingsMsg}
          </td>
        </tr>
      );
    }

    return currentListings.map(listing => {
      const props = { ...prepareListingProps(listing), ...commonProps };
      const key = listing.id.uuid;
      return isMobileLayout ? (
        <TableGrid key={key} {...props} />
      ) : (
        <TableRow key={key} {...props} />
      );
    });
  }, [currentListings, isMobileLayout, commonProps, activeTab, listingsAreLoading]);

  const infoMessage = useMemo(() => {
    console.log("listingsAreLoading", listingsAreLoading);
    console.log("activeTab", activeTab);
    console.log("shopifyConnected", shopifyConnected);

    if (listingsAreLoading || activeTab !== ListingState.SOLD || !shopifyConnected) {
      return null;
    }

    return (
      <div className={css.infoMsg}>Here you will see all products that have sold directly on your shopify store or via rareboots marketplace</div>
    );
  }, [activeTab, shopifyConnected, listingsAreLoading]);

  return (
    <div className={css.container}>
      {isArrayLength(selectedItems) && (
        <ActionBarButtons
          selectedItems={selectedItems}
          activeState={activeTab}
          dispatch={dispatch}
          config={config}
          openModal={openModal}
          setSelectedItems={setSelectedItems}
          isShopifyAccountPage={isShopifyAccountPage}
        />
      )}
      <TableSelection
        getTabState={getTabState}
        handleState={handleState}
        activeState={activeTab}
        categorizedListings={categorizedListings}
        isShopifyAccountPage={isShopifyAccountPage}
        listingsAreLoading={listingsAreLoading}
      />
      {infoMessage}
      {ShopSearchBar}
      {isMobileLayout ? (
        <>
          {hasCurrentListings && (
            <label className={css.mobileSelectAll}>
              <input
                type="checkbox"
                className={css.checkbox}
                checked={areAllCurrentListingsSelected}
                onChange={handleSelectAllCurrentListings}
              />
              <span>{areAllCurrentListingsSelected ? 'Unmark all' : 'Mark all'}</span>
            </label>
          )}
          <div className={css.listingSec}>{listingsContent}</div>
        </>
      ) : (
        <div className={css.table}>
          <table>
            <thead>
              <tr>
                <th>
                  <span className={css.checkboxHeader}>
                    {hasCurrentListings && (
                      <input
                        type="checkbox"
                        className={css.checkbox}
                        checked={areAllCurrentListingsSelected}
                        onChange={handleSelectAllCurrentListings}
                        aria-label={
                          areAllCurrentListingsSelected
                            ? 'Unmark all listings'
                            : 'Mark all listings'
                        }
                      />
                    )}
                  </span>
                </th>
                {ListingTableHeaders.map(header => (
                  <th key={header}>{header}</th>
                ))}
              </tr>
            </thead>
            <tbody>{listingsContent}</tbody>
          </table>
        </div>
      )}
      <EditListingModal
        dispatch={dispatch}
        intl={intl}
        config={config}
        isOpen={isEditListingModalOpen}
        onClose={() => {
          setIsEditListingModalOpen(false);
          setSelectedItems([]);
        }}
        selectedItems={selectedItems}
        values={values}
        form={form}
        isShopifyAccountPage={isShopifyAccountPage}
      />
    </div>
  );
};

export default ListingsTable;
